import { Box, Button, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { useNotifications } from '@/hooks/use-notifications';
import { useState, useMemo, useEffect, startTransition } from 'react';
import type { SessionDto } from '@/types/dtos/sessions/session.dto';
import type { FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import { useFlowSyncTransport } from '@/state/mutations/flows/use-flow-sync-transport';
import { arrayMove } from '@dnd-kit/sortable';
import { minuteOffsetToTime } from '@/components/events/helpers/minute-offset-to-time.helper';
import useUpdateSessionMutation from '@/state/mutations/sessions/use-update-session';
import type { UpdateSessionDto } from '@/types/dtos/sessions/request/update-session.dto';
import { SESSION_USAGE_ENTITIES } from '@/types/dtos/sessions/request/session-usage-entities.enum';

const DuplicateSessionForm = ({
  onClose,
  onSuccess,
  offset,
  durationMinutes,
  sessionsOverride,
  setDuration,
  setSelectedSlot,
  isUntimed = false,
  insertBeforeSlotId,
}: {
  onClose: () => void;
  onSuccess?: () => void;
  offset: number;
  durationMinutes: number;
  sessionsOverride?: SessionDto[];
  setDuration: React.Dispatch<React.SetStateAction<string>>;
  setSelectedSlot: (slot?: FlowSlotDto) => void;
  isUntimed?: boolean;
  insertBeforeSlotId?: string;
}) => {
  const t = useTranslations();
  const notification = useNotifications();
  const flow = useFlowStore((s) => s.flow);
  const [addingPending, setAddingPending] = useState(false);
  const { emitFlowSync, canEmit } = useFlowSyncTransport();
  const { mutateAsync: updateSessionMutation, isPending: isUpdatingSession } = useUpdateSessionMutation();

  const sessions = useMemo(() => {
    const uniqueSessionIds = new Map<string, boolean>();
    const filteredSessions = (sessionsOverride ??
      flow?.slots
        ?.filter((slot) => slot.contentType === 'Session')
        ?.filter((slot) => {
          const sessionId = slot.content?.id;
          if (!sessionId) return false;
          if (uniqueSessionIds.has(sessionId)) return false;
          uniqueSessionIds.set(sessionId, true);
          return true;
        })
        .map((e) => e.content) ??
      []) as SessionDto[];

    return filteredSessions.sort((a, b) => (a.title ?? '').localeCompare(b.title ?? ''));
  }, [flow?.slots, sessionsOverride]);
  const [selectedSessionId, setSelectedSessionId] = useState<string>(sessions[0]?.id ?? '');

  // Sync selectedSessionId when sessions change
  useEffect(() => {
    if (sessions.length > 0 && !sessions.find((session) => session.id === selectedSessionId)) {
      startTransition(() => {
        setSelectedSessionId(sessions[0].id);
      });
    }
  }, [sessions, selectedSessionId]);

  useEffect(() => {
    const selectedSession = sessions.find((session) => session.id === selectedSessionId);
    setDuration(minuteOffsetToTime(selectedSession?.plannedDurationMinutes ?? 0));
  }, [selectedSessionId, sessions, setDuration]);

  const onSubmit = async () => {
    if (!flow?.id || !selectedSessionId) {
      notification.notifyError(t('session.notification.createError'));
      return;
    }

    setAddingPending(true);

    const selectedSession = sessions.find((session) => session.id === selectedSessionId);
    if (!selectedSession) return;

    // Generate unique ID using crypto API if available, fallback to timestamp + random
    const generateTempId = () => {
      if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return `temp-${crypto.randomUUID()}`;
      }
      const timestamp = Date.now();
      const random = Math.random().toString(36).substring(7);
      return `temp-${timestamp}-${random}`;
    };
    const tempSlotId = generateTempId();
    const newSlot: FlowSlotDto = {
      id: tempSlotId,
      contentType: 'Session',
      content: selectedSession,
      computedDurationMinutes: durationMinutes,
      plannedDurationMinutes: durationMinutes,
      isUntimed: isUntimed,
      ...(isUntimed
        ? { plannedOffsetMinutes: undefined, computedOffsetMinutes: undefined }
        : { computedOffsetMinutes: offset }),
    };
    const newSlots = [...(flow.slots ?? []), newSlot];
    let insertionIndex: number;

    if (isUntimed) {
      if (insertBeforeSlotId) {
        insertionIndex = flow?.slots?.findIndex((slot) => slot.id === insertBeforeSlotId) ?? -1;
      } else {
        insertionIndex =
          flow?.slots?.findIndex(
            (slot) =>
              slot.isUntimed ?? (slot.plannedOffsetMinutes === undefined && slot.computedOffsetMinutes === undefined),
          ) ?? -1;
      }
      if (insertionIndex === -1) {
        insertionIndex = flow?.slots?.length ?? newSlots.length - 1;
      }
    } else {
      insertionIndex = flow?.slots?.findIndex((slot) => (slot.computedOffsetMinutes ?? 0) > (offset ?? 0));
      if (insertionIndex === -1) {
        insertionIndex = flow?.slots?.length;
      }
    }
    const swappedSlots = arrayMove(newSlots, newSlots.length - 1, insertionIndex);
    const normalizedSlots = swappedSlots.map((slot) => {
      const normalized = {
        ...slot,
        content:
          slot.contentType === 'Gap' ? undefined : typeof slot.content === 'object' ? slot.content?.id : slot.content,
      };
      if (slot.id === tempSlotId) {
        delete (normalized as { id?: string }).id;
      }
      return normalized;
    });

    // Extract startTime and endTime from slot if present (matching syncFlow pattern)
    // This preserves the fixed end-time (durationPlannedMinutes) when adding slots
    let startTime: string | undefined;
    let endTime: string | null | undefined;

    if (flow.slot?.startOffsetMinutes !== undefined) {
      startTime = minuteOffsetToTime(flow.slot.startOffsetMinutes);

      // Use flow.durationPlannedMinutes as primary source (flow is source of truth)
      const flowDurationPlannedMinutes = flow.durationPlannedMinutes;
      // Check if durationPlannedMinutes is explicitly set and greater than 0
      if (flowDurationPlannedMinutes !== undefined && flowDurationPlannedMinutes > 0) {
        // Fixed end-time is set
        endTime = minuteOffsetToTime(flow.slot.startOffsetMinutes + flowDurationPlannedMinutes);
      } else {
        // Fixed end-time is being removed (undefined, null, or <= 0) - explicitly set to null to clear it
        endTime = null;
      }
    }

    if (!canEmit) {
      notification.notifyError(t('session.notification.createError'));
      setAddingPending(false);
      return;
    }
    const { ok } = await emitFlowSync({
      flow: {
        ...flow,
        slots: normalizedSlots,
        slot: undefined,
        startDate: undefined,
        ...(startTime && { startTime }),
        ...(startTime !== undefined && { endTime: endTime ?? null }),
      } as Record<string, unknown>,
    });
    if (ok) {
      const newSessionId = selectedSession.id;

      notification.notifySuccess(t('session.notification.createSuccess', { title: selectedSession?.title || 'N/A' }));

      setTimeout(() => {
        const updatedFlow = useFlowStore.getState().flow;
        if (updatedFlow?.slots) {
          const newSlot = updatedFlow.slots.find(
            (slot) => slot.contentType === 'Session' && slot.content?.id === newSessionId,
          );
          if (newSlot) {
            setSelectedSlot(newSlot);
          }
        }
      }, 300);

      await updateSession(selectedSession, flow.id);
      onSuccess?.();
    } else {
      notification.notifyError(t('session.notification.createError'));
    }
    setAddingPending(false);
  };

  const updateSession = async (session: SessionDto, flowId: string) => {
    const isAlreadyAdded = session.usedInEntities?.some(
      (e) => e.entity.id === flowId && e.entityType === SESSION_USAGE_ENTITIES.FLOW,
    );
    if (isAlreadyAdded) return;

    const updateSessionDto: UpdateSessionDto = {
      id: session.id,
      version: session.version,
      usedInEntities: [
        ...(session.usedInEntities
          ? session.usedInEntities.map((e) => ({ entity: e.entity.id, entityType: e.entityType }))
          : []),
        { entity: flowId, entityType: SESSION_USAGE_ENTITIES.FLOW },
      ],
    };

    try {
      await updateSessionMutation(updateSessionDto);
    } catch {
      notification.notifyError(t('session.notification.createError'));
    }
  };

  return (
    <Box
      sx={{
        mt: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        width: '100%',
        p: 2,
      }}
    >
      <FormControl fullWidth>
        <InputLabel id="session-select-label">{t('flows.detail.slotList.actions.session.title')}</InputLabel>
        <Select
          labelId="session-select-label"
          value={selectedSessionId || ''}
          label={t('flows.detail.slotList.actions.session.title')}
          onChange={(e) => setSelectedSessionId(e.target.value)}
        >
          {sessions.map((session) => (
            <MenuItem key={session.id} value={session.id}>
              {`${session.title}, ${session.plannedDurationMinutes ?? 0} min`}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button variant="outlined" color="info" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="info"
          onClick={onSubmit}
          disabled={!selectedSessionId || addingPending || isUpdatingSession}
        >
          {t('common.save')}
        </Button>
      </Box>
    </Box>
  );
};

export default DuplicateSessionForm;
